import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as z from "zod";
import { useEffect } from "react";
import Container from "@src/components/shared/Container";
import CountrySelect from "@src/components/shared/CountrySelect";
import TitleAndDesc from "@src/components/shared/TitleAndDesc";
import { Button } from "@src/components/ui/button";
import { Checkbox } from "@src/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@src/components/ui/form";
import { Input } from "@src/components/ui/input";
import useZustandStore from "@src/helpers/zustand";
import {
  GEO_LAT_LETTERS_ONLY,
  DIGIT_SPACE_DASH_LATIN_GEORGIAN_ONLY_REGEX,
} from "@src/helpers/regex";
import { useKYCAddUserLog , LoggerTypes } from "@src/api/useKYCAddUserLog";

export const LATIN_DIGIT_ONLY_REGEX = /^[a-zA-Z0-9]*$/;

const FormSchema = z
  .object({
    legalCountry: z.number().refine((val) => val > 0),
    legalCity: z.string().refine((val) => val.length),
    legalAddress: z.string().refine((val) => val.length),
    factCountryID: z.number().optional().default(0),
    factCity: z.string().optional().default(""),
    factAddress: z.string().optional().default(""),
    check: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (data.check) {
      if (data.factCountryID <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "",
          path: ["factCountryID"], // Path points to the field with the issue
        });
      }

      if (!data.factCity.trim().length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "",
          path: ["factCity"],
        });
      }

      if (!data.factAddress.trim().length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "",
          path: ["factAddress"],
        });
      }
    }
  });

function Address() {
  const addUserLogMutation = useKYCAddUserLog();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: zData, setData } = useZustandStore((state) => state);

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      legalCountry: zData.address.legalCountry,
      legalCity: zData.address.legalCity,
      legalAddress: zData.address.legalAddress,
      check: zData.address.check,
      factCountryID: zData.address.check
        ? zData.address.factCountryID
        : zData.address.legalCountry,
      factCity: zData.address.check
        ? zData.address.factCity
        : zData.address.legalCity,
      factAddress: zData.address.check
        ? zData.address.factAddress
        : zData.address.legalAddress,
    },
  });
  useEffect(() => {
    addUserLogMutation.mutate({
      eventName: LoggerTypes.Address,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onSubmit(address: z.infer<typeof FormSchema>) {
    setData({
      ...zData,
      address,
    });
    navigate(`/${i18n.language}/employment`);
  }

  const [check] = form.watch(["check"]);

  return (
    <Container className="max-md:min-h-[90vh] max-md:bg-white">
      <Form {...form}>
        <div className="py-4">
          <TitleAndDesc desc={t("address.desc")} title={t("address.title")} />
        </div>
        <form
          className="flex flex-col gap-3 max-md:mb-40"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="flex flex-col gap-5">
            <FormField
              control={form.control}
              name="legalCountry"
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormControl>
                      <CountrySelect
                        countryId={field.value}
                        label={t("address.country")}
                        setCountryId={(newCountryId) => {
                          if (!check) {
                            form.setValue("factCountryID", newCountryId);
                          }
                          field.onChange({
                            target: {
                              value: newCountryId,
                            },
                          });
                        }}
                      />
                    </FormControl>
                  </FormItem>
                );
              }}
            />
            <FormField
              control={form.control}
              name="legalCity"
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...field}
                        label={t("address.city")}
                        onChange={(e) => {
                          const { target } = e;
                          if (
                            GEO_LAT_LETTERS_ONLY.test(target.value) ||
                            !target.value
                          ) {
                            if (!check) {
                              form.setValue("factCity", target.value);
                            }
                            field.onChange(e);
                          }
                        }}
                      />
                    </FormControl>
                  </FormItem>
                );
              }}
            />
            <FormField
              control={form.control}
              name="legalAddress"
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...field}
                        label={t("address.address")}
                        onChange={(e) => {
                          const { target } = e;
                          if (
                            DIGIT_SPACE_DASH_LATIN_GEORGIAN_ONLY_REGEX.test(
                              target.value
                            ) ||
                            !target.value
                          ) {
                            if (!check) {
                              form.setValue("factAddress", target.value);
                            }
                            field.onChange(e);
                          }
                        }}
                      />
                    </FormControl>
                  </FormItem>
                );
              }}
            />

            <div>
              <FormField
                control={form.control}
                name="check"
                render={({ field }) => {
                  return (
                    <FormItem className="items-top flex w-full gap-2">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          className="!mt-0"
                          onCheckedChange={(value) => {
                            if (value) {
                              form.setValue("factCountryID", 0);
                              form.setValue("factCity", "");
                              form.setValue("factAddress", "");
                            }
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormLabel className="!mt-0 text-sm font-normal leading-6 text-gray-600 cursor-pointer">
                        {t("address.checkbox.desc")}
                      </FormLabel>
                    </FormItem>
                  );
                }}
              />
            </div>
            {check ? (
              <div className="flex flex-col gap-5">
                <TitleAndDesc title={t("address.current")} />

                <FormField
                  control={form.control}
                  name="factCountryID"
                  render={({ field }) => {
                    return (
                      <FormItem>
                        <FormControl>
                          <CountrySelect
                            countryId={field.value}
                            label={t("address.country")}
                            setCountryId={(newCountryId) => {
                              form.setValue("factCountryID", newCountryId);
                            }}
                          />
                        </FormControl>
                      </FormItem>
                    );
                  }}
                />

                <FormField
                  control={form.control}
                  name="factCity"
                  render={({ field }) => {
                    return (
                      <FormItem>
                        <FormControl>
                          <Input
                            {...field}
                            label={t("address.city")}
                            onChange={(e) => {
                              const { target } = e;
                              if (
                                GEO_LAT_LETTERS_ONLY.test(target.value) ||
                                !target.value
                              ) {
                                field.onChange(e);
                              }
                            }}
                          />
                        </FormControl>
                      </FormItem>
                    );
                  }}
                />

                <FormField
                  control={form.control}
                  name="factAddress"
                  render={({ field }) => {
                    return (
                      <FormItem>
                        <FormControl>
                          <Input
                            {...field}
                            label={t("address.address")}
                            onChange={(e) => {
                              const { target } = e;
                              if (
                                DIGIT_SPACE_DASH_LATIN_GEORGIAN_ONLY_REGEX.test(
                                  target.value
                                ) ||
                                !target.value
                              ) {
                                field.onChange(e);
                              }
                            }}
                          />
                        </FormControl>
                      </FormItem>
                    );
                  }}
                />
              </div>
            ) : null}
          </div>
          <div className="z-10 max-md:fixed max-md:bottom-0 max-md:left-0 max-md:right-0">
            <div className="rounded-6 w-full self-end bg-white py-8 max-md:px-4 ">
              <Button
                className="w-full"
                disabled={!form.formState.isValid}
                type="submit"
              >
                {t("continue")}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </Container>
  );
}

export default Address;
